import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  ConversationalFormInputType,
  LiveTranscript,
  RecordingState,
} from '@karehero/models';

import { Icon } from '@/components/atoms/Icon/Icon';
import { radius } from '@/theme/radius';
import { linesOfText } from './CardEditSheet';
import { ComposerPhotos } from './ComposerPhotos';
import { LiveCaptionCard } from './LiveCaptionCard';
import { OrbDock } from './OrbDock';
import { useIsWrapped } from './useIsWrapped';
import { VoiceNoteBubble } from './VoiceNoteBubble';

export interface RecorderProps {
  state: RecordingState;
  durationSeconds: number;
  /** Sound levels sampled so far, 0–1. */
  levels: number[];
  /** Recording stops at this cap. */
  maxDurationSeconds?: number;
  error: string | null;
  /** The finished recording, waiting to be sent. */
  file: File | null;
  isPlaying: boolean;
  /** Captions of the recording as it happens, where offered. */
  liveTranscript?: LiveTranscript;
  /** Their correction of the captions, sent in place of them. */
  editedTranscript?: string | null;
  /** Offers to correct the captions before the recording is sent. */
  onEditTranscript?: (text: string) => void;
  /** Whether the captions are shown; they are made either way. Defaults to shown. */
  isCaptionShown?: boolean;
  /** Shows or hides the captions; omit to always show them. */
  onToggleCaption?: () => void;
  onStart: () => void;
  onStop: () => void;
  onTogglePlay: () => void;
  onDelete: () => void;
  onSend: () => void;
}

/** Record from the orb above the bar, or from a button beside the field. */
export type VoiceControl = 'orb' | 'button';

/** A card's lines being corrected in the message field, saved with its send button. */
export interface CardEditing {
  /** The card being corrected; another card starts its text afresh. */
  id: string;
  title: string;
  lines: string[];
  /** Called with the corrected lines, one per paragraph. */
  onSave: (lines: string[]) => void;
  onCancel: () => void;
}

export interface ChatComposerHandle {
  /** Send any typed text, photos and recording waiting to be sent. */
  flushDraft: () => void;
}

export interface ChatComposerProps {
  /** Typed text is dropped when this changes. */
  stepId?: string;
  placeholder?: string;
  /** What is typed, so a phone number gets the phone keypad. */
  inputType?: ConversationalFormInputType;
  /** Omit to hide the orb. */
  recorder?: RecorderProps;
  /** Defaults to the orb. */
  voiceControl?: VoiceControl;
  isFileEnabled?: boolean;
  isSubmitting?: boolean;
  /** Photos waiting to go with the next message. */
  photos?: File[];
  setPhotos?: (photos: File[]) => void;
  onSend: (text: string, photos: File[]) => void;
  /** Hears the typed text as it changes, before it is sent. */
  onDraftChange?: (text: string) => void;
  /** Hears the message field lose focus, as when the keyboard is closed. */
  onDraftBlur?: () => void;
  onAttachFile?: () => void;
  /** Expose flushDraft so a sibling (e.g. ActionRows) can send the draft. */
  handleRef?: React.RefObject<ChatComposerHandle | null>;
  /** A card being corrected in the field; the bar only saves it until it is done. */
  editing?: CardEditing;
}

/** A blank line between paragraphs, as they are read back. */
const PARAGRAPH_BREAK = '\n\n';

const DEFAULT_PLACEHOLDER = 'Type a message…';

/** Names the message field where neither a label nor a placeholder does. */
const MESSAGE_FIELD_LABEL = 'Message';

const NO_PHOTOS: File[] = [];

const ComposerRow = ({ children }: { children: ReactNode }) => (
  <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
    {children}
  </Stack>
);

const SendButton = ({
  label,
  isDisabled,
  onClick,
}: {
  label: string;
  isDisabled?: boolean;
  onClick: () => void;
}) => (
  <IconButton
    aria-label={label}
    disabled={isDisabled}
    onClick={onClick}
    variant='chatControl'
  >
    <Icon icon='paper-plane-top' fontSize={16} />
  </IconButton>
);

const RecordButton = ({
  isDisabled,
  onClick,
}: {
  isDisabled?: boolean;
  onClick: () => void;
}) => (
  <IconButton
    aria-label='Record a voice note'
    disabled={isDisabled}
    onClick={onClick}
    variant='chatControl'
  >
    <Icon icon='microphone' fontSize={16} />
  </IconButton>
);

/** A quiet switch for the captions, in the bar, so nothing jumps in as recording starts. */
const CaptionToggle = ({ recorder }: { recorder: RecorderProps }) => {
  if (!recorder.liveTranscript || !recorder.onToggleCaption) return null;
  const isShown = recorder.isCaptionShown !== false;
  return (
    <IconButton
      aria-label={isShown ? 'Hide live captions' : 'Show live captions'}
      aria-pressed={isShown}
      onClick={recorder.onToggleCaption}
      sx={{
        color: isShown ? 'primary.main' : 'text.disabled',
        bgcolor: isShown ? 'action.selected' : 'transparent',
      }}
    >
      <Icon icon='closed-captioning' fontSize={18} />
    </IconButton>
  );
};

const RecordingRow = ({
  recorder,
  isSubmitting,
}: {
  recorder: RecorderProps;
  isSubmitting?: boolean;
}) => (
  <ComposerRow>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <VoiceNoteBubble
        tone='recording'
        durationSeconds={recorder.durationSeconds}
        levels={recorder.levels}
      />
    </Box>
    <CaptionToggle recorder={recorder} />
    <IconButton
      aria-label='Stop recording'
      disabled={isSubmitting}
      onClick={recorder.onStop}
      variant='chatControl'
    >
      <Icon icon='square' fontSize={14} />
    </IconButton>
  </ComposerRow>
);

const DraftRow = ({
  recorder,
  isSubmitting,
}: {
  recorder: RecorderProps;
  isSubmitting?: boolean;
}) => (
  <ComposerRow>
    <IconButton
      aria-label='Delete recording'
      color='error'
      disabled={isSubmitting}
      onClick={recorder.onDelete}
    >
      <Icon icon='trash' fontSize={16} />
    </IconButton>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <VoiceNoteBubble
        tone='outlined'
        durationSeconds={recorder.durationSeconds}
        levels={recorder.levels}
        isPlaying={recorder.isPlaying}
        onTogglePlay={recorder.onTogglePlay}
      />
    </Box>
    <CaptionToggle recorder={recorder} />
    <SendButton
      label='Send recording'
      isDisabled={isSubmitting}
      onClick={recorder.onSend}
    />
  </ComposerRow>
);

/** A message to send, or a card's lines being corrected, which may run to paragraphs. */
type TextRowMode = 'message' | 'correction';

/** The keypad and autofill each kind of answer asks the phone for. */
const INPUT_HINTS: Record<
  ConversationalFormInputType,
  { inputMode?: 'tel'; autoComplete?: 'tel' }
> = {
  text: {},
  tel: { inputMode: 'tel', autoComplete: 'tel' },
};

/** How tall the field grows before it scrolls, by what it holds. */
const MAX_ROWS: Record<TextRowMode, number> = { message: 4, correction: 12 };

const TextRow = ({
  text,
  hasPhotos,
  placeholder,
  isFileEnabled,
  isSubmitting,
  onChange,
  onBlur,
  onSend,
  onAttachFile,
  onRecord,
  mode = 'message',
  inputLabel,
  inputType = 'text',
}: {
  text: string;
  hasPhotos: boolean;
  placeholder?: string;
  inputType?: ConversationalFormInputType;
  isFileEnabled?: boolean;
  isSubmitting?: boolean;
  onChange: (text: string) => void;
  onBlur?: () => void;
  onSend: () => void;
  onAttachFile?: () => void;
  /** Records instead of sending while nothing is typed. Omit to always send. */
  onRecord?: () => void;
  /** A correction saves rather than sends, and Enter starts a new line. */
  mode?: TextRowMode;
  /** Names the field, where its placeholder does not. */
  inputLabel?: string;
}) => {
  const isCorrection = mode === 'correction';
  const shownPlaceholder = placeholder ?? DEFAULT_PLACEHOLDER;
  const label =
    inputLabel ?? (shownPlaceholder ? undefined : MESSAGE_FIELD_LABEL);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // A pill on one line; a rounded rectangle once the message wraps. A card's
  // lines being corrected take the captions card's corners from the start.
  const isWrapped = useIsWrapped(textareaRef, text);
  const corner = isCorrection ? radius.md : isWrapped ? radius.xl : radius.xxl;

  return (
    <ComposerRow>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          placeholder={shownPlaceholder}
          value={text}
          disabled={isSubmitting}
          multiline
          maxRows={MAX_ROWS[mode]}
          inputRef={textareaRef}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={(e) => {
            // A correction keeps its paragraphs, so Enter starts a new line.
            if (e.key === 'Enter' && !e.shiftKey && !isCorrection) {
              e.preventDefault();
              onSend();
            }
          }}
          slotProps={{
            htmlInput: {
              ...(label && { 'aria-label': label }),
              ...INPUT_HINTS[inputType],
            },
            input: {
              startAdornment:
                isFileEnabled && onAttachFile && !isCorrection ? (
                  <InputAdornment position='start'>
                    <IconButton
                      aria-label='Add a photo or a file'
                      size='small'
                      disabled={isSubmitting}
                      onClick={onAttachFile}
                    >
                      <Icon icon='plus' fontSize={18} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            },
          }}
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: `${corner}px`,
              transition: theme.transitions.create('border-radius'),
              pl: 1,
            },
          })}
        />
      </Box>
      {isCorrection ? (
        <SendButton
          label='Save changes'
          isDisabled={!text.trim() || isSubmitting}
          onClick={onSend}
        />
      ) : onRecord && !text.trim() && !hasPhotos ? (
        <RecordButton isDisabled={isSubmitting} onClick={onRecord} />
      ) : (
        <SendButton
          label='Send'
          isDisabled={(!text.trim() && !hasPhotos) || isSubmitting}
          onClick={onSend}
        />
      )}
    </ComposerRow>
  );
};

/** Says a card is being corrected, above the field, with a way to stop. */
const EditingLine = ({ onCancel }: { onCancel: () => void }) => (
  <Stack
    direction='row'
    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
  >
    <Stack
      direction='row'
      spacing={0.75}
      sx={{ alignItems: 'center', color: 'primary.main' }}
    >
      <Icon icon='pencil' fontSize={12} />
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        Editing
      </Typography>
    </Stack>
    <IconButton aria-label='Stop editing' size='small' onClick={onCancel}>
      <Icon icon='xmark' fontSize={12} />
    </IconButton>
  </Stack>
);

/** A card's lines put in the field to correct, saved with its send button. */
const EditingBar = ({
  editing,
  placeholder,
  isSubmitting,
}: {
  editing: CardEditing;
  placeholder?: string;
  isSubmitting?: boolean;
}) => {
  // Opened afresh for each card, from its lines as they are now.
  const [text, setText] = useState(editing.lines.join(PARAGRAPH_BREAK));
  return (
    <>
      <EditingLine onCancel={editing.onCancel} />
      <TextRow
        text={text}
        hasPhotos={false}
        placeholder={placeholder}
        isSubmitting={isSubmitting}
        onChange={setText}
        onSend={() => editing.onSave(linesOfText(text))}
        mode='correction'
        inputLabel={`Correct ${editing.title}`}
      />
    </>
  );
};

/** ChatComposer is the input bar for an `expand` or `say` step: type, or record with the orb or the button. */
export const ChatComposer = ({
  stepId,
  placeholder,
  inputType = 'text',
  recorder,
  voiceControl = 'orb',
  isFileEnabled,
  isSubmitting,
  photos = NO_PHOTOS,
  setPhotos,
  onSend,
  onDraftChange,
  onDraftBlur,
  onAttachFile,
  handleRef,
  editing,
}: ChatComposerProps) => {
  // Kept here so typed text survives TextRow unmounting while recording, and
  // tied to its step so the composer can stay mounted from one step to the next.
  const [draft, setDraft] = useState({ stepId, text: '' });
  const text = draft.stepId === stepId ? draft.text : '';
  const setText = (next: string) => setDraft({ stepId, text: next });

  useEffect(() => {
    onDraftChange?.(text);
  }, [onDraftChange, text]);
  // Put away with the composer, the typed text goes too.
  useEffect(() => () => onDraftChange?.(''), [onDraftChange]);

  const handleSend = () => {
    if (!text.trim() && !photos.length) return;
    onSend(text.trim(), photos);
    setText('');
    if (photos.length) setPhotos?.([]);
  };

  const isRecording = recorder?.state === 'recording';
  const hasDraft = recorder?.state === 'stopped' && !!recorder.file;
  const isOrb = voiceControl === 'orb';

  useImperativeHandle(handleRef, () => ({
    flushDraft: () => {
      handleSend();
      if (recorder && hasDraft) recorder.onSend();
    },
  }));

  return (
    // The orb is put away while a recording or a photo waits to be sent, so
    // the two never wait together. With the button there is no orb at all.
    <OrbDock
      recorder={isOrb ? recorder : undefined}
      isWanted={isOrb && !!recorder && !hasDraft && !photos.length && !editing}
      isSubmitting={isSubmitting}
    >
      <Stack spacing={1}>
        {photos.length > 0 && (
          <ComposerPhotos
            photos={photos}
            isDisabled={isSubmitting}
            onRemove={(photo) =>
              setPhotos?.(photos.filter((held) => held !== photo))
            }
          />
        )}

        {recorder?.error && (
          <Typography
            variant='caption'
            align='center'
            sx={{ color: 'error.main' }}
          >
            {recorder.error}
          </Typography>
        )}

        {recorder?.liveTranscript &&
          (isRecording || hasDraft) &&
          recorder.isCaptionShown !== false && (
            <LiveCaptionCard
              transcript={recorder.liveTranscript}
              editedText={recorder.editedTranscript}
              onEdit={hasDraft ? recorder.onEditTranscript : undefined}
            />
          )}

        {editing ? (
          <EditingBar
            key={editing.id}
            editing={editing}
            placeholder={placeholder}
            isSubmitting={isSubmitting}
          />
        ) : recorder && isRecording ? (
          <RecordingRow recorder={recorder} isSubmitting={isSubmitting} />
        ) : recorder && hasDraft ? (
          <DraftRow recorder={recorder} isSubmitting={isSubmitting} />
        ) : (
          <TextRow
            text={text}
            hasPhotos={photos.length > 0}
            placeholder={placeholder}
            inputType={inputType}
            isFileEnabled={isFileEnabled}
            isSubmitting={isSubmitting}
            onChange={setText}
            onBlur={onDraftBlur}
            onSend={handleSend}
            onAttachFile={onAttachFile}
            onRecord={!isOrb ? recorder?.onStart : undefined}
          />
        )}
      </Stack>
    </OrbDock>
  );
};

ChatComposer.displayName = 'ChatComposer';
export default ChatComposer;
